import React, { useState } from "react";
import { Stack } from "react-bootstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { TableC } from "../../../CommonElements/TableC";
import { useHousingContractsQuery } from "@services/housing";
import { LabelValue } from "@Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { UserGroup03Icon } from "@hugeicons/core-free-icons";
import { useFilter } from "@/CommonElements/TableC/hooks/useFilter";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { DATE_FORMAT, DATE_TIME_FORMAT, permissions } from "@/utils/constants";
import { useSystem } from "@/utils/hooks/useLocation";
import usePermissions from "@/utils/hooks/usePermissions";

const HousingContracts = () => {
  // ==========
  // Hooks
  // ==========
  const { getLocalizedNumber } = useLocalizedText();
  const [currentPage, setCurrentPage] = useState(1);
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const {
    hasAnyPermission
  } = usePermissions();
  const { currentSystem, city_id, from_id } = useSystem();
  const { filters, handleFilterApply, handleFilterReset } = useFilter({ city_id });
  // Wrap handleFilterApply to work with TableC
  const onFilterApply = ({city_id: _, ...filterData}) => {
    handleFilterApply(() => {
      setCurrentPage(1); // Reset to first page when applying filters
    })({city_id, ...filterData});
  };

  const onFilterReset = () => {
    handleFilterReset(() => {
      setCurrentPage(1); // Reset to first page when resetting filters
    })();
  };

  // =========
  // Queries
  // =========
  const { data: housingContractsRes, isLoading: isHousingContractsLoading } =
    useHousingContractsQuery({
      search: debouncedValue,
      page: currentPage,
      per_page: 10,
      ...filters,
    });

  const meta = housingContractsRes?.data?.meta;
  const housingContracts = housingContractsRes?.data?.data || [];
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();

  const isLoading = isHousingContractsLoading;
  const columns = [
    {
      name: t("CONTRACT_NUMBER"),
      key: "id",
      render: (value) => (
        <Link className="fw-bold" to={`${value}/view`}>
          {getLocalizedNumber(value)}
        </Link>
      ),
    },
    {
      name: t("CONTRACT_START_DATE"),
      key: "contractStartDate",
      render: (value) => getLocalizedDate(value, DATE_FORMAT),
    },
    {
      name: t("CONTRACT_END_DATE"),
      key: "contractEndDate",
      render: (value) => getLocalizedDate(value, DATE_FORMAT),
    },
    {
      name: t("REQUEST_ID"),
      key: "RequestId",
      render: (value) => <LabelValue.Value value={value || "-"} />,
    },
    {
      name: t("PERMIT_NUMBER"),
      key: "permit_no",
      render: (value) => (
        <LabelValue copyOnValueClick copyValue={value} value={value} />
      ),
    },
    {
      name: t("BUILDING_NAME"),
      key: "houseCommercialNameAr",
    },
    {
      name: t("BUILDING_CITY"),
      key: "cityName",
    },
    {
      name: t("PILGRIMS_COUNT"),
      key: "numberOfPilgrims",
    },
    {
      name: t("ORGANIZER"),
      key: "missionNameEn",
      render: (value, rowData) => (
        <LabelValue.Value
          value={
            getLocalizedText(rowData.missionNameAr, rowData.missionNameEn) ||
            "-"
          }
        />
      ),
    },
    {
      name: t("AFFILIATED_ORGANIZER"),
      key: "companyNameAr",
      render: (value, rowData) => (
        <LabelValue.Value
          value={
            getLocalizedText(rowData.companyNameAr, rowData.companyNameEn) ||
            "-"
          }
        />
      ),
    },
    hasAnyPermission([permissions.view_housing_contract_details]) && {
      name: t("ACTIONS"),
      key: "actions",
      filterable: false,
    },
  ].filter(Boolean) ;

  const filterFields = [
    {
      name: "isEmpty",
      label: t("BUILDING_STATUS"),
      type: "select",
      options: [
        { label: t("BUILDING_EMPTY"), value: "0" },
        { label: t("BUILDING_NOT_EMPTY"), value: "1" },
      ],
    },
    {
      name: "area_no",
      label: t("BUILDING_ZONE_NUMBER"),
      type: "input",
    },
    {
      name: "permit_no",
      label: t("PERMIT_NUMBER"),
      type: "input",
    },
    {
      name: "house_name",
      label: t("BUILDING_CODE"),
      type: "input",
    },
    {
      name: "houseContract_id",
      label: t("BUILDING_CONTRACT_NUMBER"),
      type: "select",
      optionKey: "contracts",
      options: (c) => {
        return {
          label: (
            <LabelValue.Value
              value={[c.id, c.house_commercial_name_ar].join(" - ")}
            />
          ),
          value: c.id,
        };
      },
    },
    {
      name: "service_center_id",
      label: t("BUILDING_SERVICE_CENTER"),
      type: "select",
      optionKey: "service_centers",
      options: (s) => {
        return {
          label: <LabelValue.Value value={s.name} />,
          value: s.id,
        };
      },
    },
    {
      name: "mission_name",
      label: t("BUILDING_ORGANIZER"),
      type: "select",
      placeholder: t("DELEGATION"),
      optionKey: "missions",
      options: (mission) => ({
        label: getLocalizedText(mission.name_ar, mission.name_en),
        value: mission.id,
      }),
    },
    {
      name: "company_name",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      placeholder: t("AFFILIATED_ORGANIZER"),
      optionKey: "companies",
      options: (company) => ({
        label: getLocalizedText(company.name_ar, company.name_en),
        value: company.id,
      }),
    },
    {
      name: "BUILDING_DELEGATE",
      label: t("BUILDING_DELEGATE"),
      type: "input",
    },
  ];

  const getActions = (rowData) => {
    return [
      {
        key: "group_and_campaigns_contracts",
        label: (
          <Link
            className="btn btn-sm btn-outline-primary"
            to={`/app/${currentSystem}/housing-contracts/${rowData.id}/view`}
          >
            <Stack
              style={{ textWrap: "nowrap" }}
              direction="horizontal"
              gap={2}
            >
              <HugeiconsIcon size={20} icon={UserGroup03Icon} />
              {t("CONTRACT_DETAILS")}
            </Stack>
          </Link>
        ),
      },
    ];
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <PageHeader title={t("HOUSING_CONTRACTS")} sticky={false} />
      <Container fluid>
        <TableC
          actions={getActions}
          data={housingContracts}
          columns={columns}
          loading={isLoading}
          pagination={
            meta?.per_page && meta?.total
              ? {
                current: currentPage,
                total: Array.isArray(meta.total) ? meta.total[0] : meta.total,
                pageSize: Array.isArray(meta.per_page)
                  ? meta.per_page[0]
                  : meta.per_page,
                onChange: handlePageChange,
              }
              : undefined
          }
          // search
          onSearchChange={(value) => {
            setSearchValue(value?.target?.value || "");
          }}
          searchValue={searchValue || ""}
          // filters
          filterFields={filterFields}
          filters={filters}
          onFilterApply={onFilterApply}
          onFilterReset={onFilterReset}
        />
      </Container>
    </div>
  );
};

export default HousingContracts;
